// The package guide's PLAN-level actions and rules: ONE copy, used by the desktop package guide
// (features/packageGuide/PackageGuideCard.jsx, the Sales Order page and the Shipping hub) and the phone package guide
// (mobile/screens/shipping/PackageGuideScreen.jsx). docs/mobile-pwa-plan.md "One copy of every action". Moved from
// PackageGuideCard word for word (2026-10-29): Ship all ready / Mark all ready for pickup (with the timeout recovery),
// part labels (one pallet / all sealed), the combined manifest, the finalize result, Find, and the plan-edit POST
// (manual-assign, a new pallet) that Prepare shipment uses. The per-pallet actions are ./palletActions.js.
//
// `ui` (features/desktopUi.js | mobile/ui/phoneUi.js, maybe wrapped by identityUi / workingAsUi): pin, confirm and
// openPdfTab(url, filename). The desktop opens PDFs in a new tab (as before); a phone has none (null), so an action
// returns the PDF as `document` for the screen to offer, and part labels go through the shop print queue to
// `ui.labelStation` (features/print/labelStation). The page's own state: setBusy(true|false), setMessage(text) ('' clears
// it), reloadPlan(), openFinalize({kind, pin, preActionBits, batchManifestUrl?, batchPalletLabels?}).
import config from '../../config.js';
import { detailToString } from '../../utils/apiError';
import { allPalletsLanded, isTransportFailure, landedPalletIds } from '../../utils/bulkPalletRecovery';
import { batchEmailNote, finalizeEmailNote } from '../../utils/emailOutcome';
import { shopToday } from '../../utils/shopDate';
import { postWithInhouseOverride, printLabelsAt } from './palletActions';
import { wipDrainNote } from './palletRules';
import { withEditor } from './planEdits';

const API_BASE = config.API_BASE_URL;
const noop = () => {};
const errText = (err) => err.message || String(err);
const notConsolidated = (plan) => ((plan && plan.pallets) || []).filter((p) => p.status !== 'Consolidated');

// --- Rules (pure) ------------------------------------------------------------------------------

/** Ship all / Mark all ready for pickup: offered on a Committed / Fulfilling plan with a Ready pallet. Consolidated
 *  pallets don't count toward "all" (their boxes were merged into another pallet). null when not offered. */
export function bulkReadyOffer(plan) {
  if (!plan || (plan.status !== 'Committed' && plan.status !== 'Fulfilling')) return null;
  const readyCount = (plan.pallets || []).filter((p) => p.status === 'Ready').length;
  if (readyCount < 1) return null;
  const totalCount = notConsolidated(plan).length;
  return { readyCount, totalCount, allReady: readyCount === totalCount && totalCount > 0, isPickup: plan.fulfillment_type === 'pickup' };
}

/** "Customer here: confirm pickup of N": a pickup order that is ReadyForPickup / Complete with a Staged pallet.
 *  allStaged = every pallet still to leave is staged. null when not offered. */
export function pickupAllOffer(plan) {
  if (!plan || plan.fulfillment_type !== 'pickup' || (plan.status !== 'ReadyForPickup' && plan.status !== 'Complete')) return null;
  const pallets = plan.pallets || [];
  const stagedCount = pallets.filter((p) => p.status === 'Staged').length;
  if (stagedCount < 1) return null;
  const allStaged = stagedCount === pallets.filter((p) => p.status === 'Staged' || p.status === 'Shipped' || p.status === 'Consolidated').length;
  return { stagedCount, allStaged };
}

/** Mirror of the API's _detect_so_finalize_batch: pallets staged / shipped AFTER the last SO finalize form a new batch
 *  the customer hasn't had a consolidated notice for (typical after /reopen + re-stage). */
export function finalizeBatchAvailable(plan) {
  if (!plan?.so_finalized_at) return false;
  const t0 = new Date(plan.so_finalized_at).getTime();
  return (plan.pallets || []).some((p) => (p.status === 'Staged' || p.status === 'Shipped')
    && p.shipment_group_id
    && (p.staged_at || p.shipped_at)
    && new Date(p.staged_at || p.shipped_at).getTime() > t0);
}

/** Which finalize the plan offers ({kind: 'shipping' | 'pickup', batch}), or null: a pickup plan once ReadyForPickup,
 *  any plan once Complete; not again after the SO was finalized unless a new batch went out since. */
export function finalizeOffer(plan) {
  if (!plan) return null;
  const open = !plan.so_finalized_at || finalizeBatchAvailable(plan);
  if (!open) return null;
  const isPickup = plan.fulfillment_type === 'pickup';
  if ((plan.status === 'ReadyForPickup' && isPickup) || plan.status === 'Complete') {
    return { kind: isPickup ? 'pickup' : 'shipping', batch: !!plan.so_finalized_at };
  }
  return null;
}

/** Every pallet shipped with per-pallet emails off and no SO finalize yet: the customer has had NO email at all. */
export function needsFinalizeNotice(plan) {
  return !!plan && plan.status === 'Complete' && plan.notify_per_pallet === false && !plan.so_finalized_at;
}

/** The pallets with a shipment record (sealed or later): part labels can print for them. */
export function sealedPallets(plan) {
  return ((plan && plan.pallets) || []).filter((p) => p.shipment_group_id);
}

/** The pallets Find all lights: a beacon on them, not shipped or merged away. */
export function findablePallets(plan) {
  return ((plan && plan.pallets) || []).filter((p) => p.beacon_id && p.status !== 'Shipped' && p.status !== 'Consolidated');
}

/** Pallets by status (Consolidated left out): {Building, Ready, Staged, Shipped, total}. */
export function palletStatusCounts(plan) {
  const out = { Building: 0, Ready: 0, Staged: 0, Shipped: 0, total: 0 };
  for (const p of notConsolidated(plan)) {
    const s = p.status || 'Building';
    if (out[s] != null) out[s] += 1;
    out.total += 1;
  }
  return out;
}

// --- Plan edits (Prepare shipment's Pack into / a new pallet) --------------------------------------

/** POST a plan edit (withEditor): re-reads the plan on success and resolves the answer ({} when it has no body);
 *  on a refusal shows the API's sentence and resolves null. */
export async function postPlanEdit({ apiBase = API_BASE, path, body, setBusy = noop, setMessage = noop, reloadPlan = noop }) {
  setBusy(true); setMessage('');
  try {
    const res = await fetch(`${apiBase}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withEditor(body)),
    });
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
    }
    await reloadPlan();
    return await res.json().catch(() => ({}));
  } catch (err) {
    setMessage(errText(err));
    return null;
  } finally {
    setBusy(false);
  }
}

/** Pack a part into a pallet / box (manual-assign). Packing takes the pieces off the laser task's WIP pallet(s): the
 *  message says how many, which pallet emptied (its beacon is freed) and any step the job still owes the part. */
export async function manualAssign({ apiBase = API_BASE, plan, body, setBusy = noop, setMessage = noop, reloadPlan = noop }) {
  if (!plan) return undefined;
  const res = await postPlanEdit({ apiBase, path: `/packaging/plans/${plan.plan_id}/manual-assign`, body, setBusy, setMessage, reloadPlan });
  const note = wipDrainNote(res);
  if (note) setMessage(note);
  return res;
}

/** A new empty pallet ('40x48', '48x96'...; anything else is 40x48). Resolves the API's answer (pallet_id) or null. */
export async function createEmptyPallet({ apiBase = API_BASE, plan, sizeChoice, setBusy = noop, setMessage = noop, reloadPlan = noop }) {
  if (!plan) return undefined;
  const sizeStr = typeof sizeChoice === 'string' ? sizeChoice : '40x48';
  const [l, w] = sizeStr.split('x').map((s) => parseFloat(s));
  return postPlanEdit({ apiBase, path: `/packaging/plans/${plan.plan_id}/pallets`, body: { length_in: l, width_in: w }, setBusy, setMessage, reloadPlan });
}

/** Find: light the beacons and show where the pallets are (the plan's Find list; `palletIds` = only those). */
export async function findPlanPallets({ plan, palletIds = null, reloadPlan = noop }) {
  if (!plan) return;
  const { showPlanPallets } = await import('../pallets/planPalletFlows');
  if (palletIds) await showPlanPallets(plan.plan_id, palletIds);
  else await showPlanPallets(plan.plan_id);
  await reloadPlan();
}

// --- Ship all / Stage all ----------------------------------------------------------------------
//
// ship-all-ready / stage-all-ready do every pallet's work inside ONE synchronous request. On a large plan that request
// can outrun the API gateway's 30s ceiling (SO-1109, 4 pallets: 43.6s): fetch() rejects while the Lambda runs on and
// COMMITS. A transport failure is therefore NOT evidence that the work failed: re-read the plan and let the server's
// state decide (utils/bulkPalletRecovery), polling because the request is often still in flight.

export const RECOVERY_POLL_ATTEMPTS = 10;
export const RECOVERY_POLL_DELAY_MS = 5000;
export const RECOVERY_FIRST_DELAY_MS = 2000;

/** Poll GET /packaging/plans/by-so/{so} until every pallet acted on reaches a terminal status. Resolves
 *  {snap, landed} once they all land, or null when they never do (the work did not happen). */
export async function pollBulkPalletOutcome({
  apiBase = API_BASE, salesOrderId, palletIds, terminalStatuses, onWaiting = null,
  attempts = RECOVERY_POLL_ATTEMPTS, firstDelayMs = RECOVERY_FIRST_DELAY_MS, delayMs = RECOVERY_POLL_DELAY_MS,
}) {
  if (!palletIds.length) return null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, attempt === 0 ? firstDelayMs : delayMs));
    let snap;
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(`${apiBase}/packaging/plans/by-so/${salesOrderId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // eslint-disable-next-line no-await-in-loop
      snap = await res.json();
    } catch (_) {
      continue; // network still flaky: keep trying
    }
    const landed = landedPalletIds(snap, palletIds, terminalStatuses);
    if (allPalletsLanded(snap, palletIds, terminalStatuses)) return { snap, landed: landed.length };
    if (onWaiting) onWaiting(landed.length, palletIds.length);
  }
  return null;
}

/** The Ship all question. `opensPdf` false (a phone): the manifest is handed to the screen instead of opening. */
export function shipAllQuestion(plan, readyCount, willCloseSO, { opensPdf = true } = {}) {
  const opens = opensPdf ? 'and that manifest opens for printing' : 'and that manifest is ready here to print or share';
  return willCloseSO
    ? `Ship all ${readyCount} pallet(s) for ${plan.order_number}?\n\n`
      + `One delivery-notification email goes out now with the manifest for these ${readyCount} pallet(s) attached, `
      + `${opens}. The SO closes out afterwards (Finalize records it; `
      + 'no second email goes out when every pallet has already been notified).'
    : `Ship ${readyCount} ready pallet(s) for ${plan.order_number}?\n\n`
      + `One delivery-notification email goes out now with the manifest for just these ${readyCount} pallet(s) attached, `
      + `${opens}. The remaining pallet(s) get their own email when they ship.`;
}

export function stageAllQuestion(plan, readyCount, willCloseSO) {
  return willCloseSO
    ? `Mark all ${readyCount} pallet(s) ready for pickup on ${plan.order_number} and send the customer the consolidated "Order Ready for Pickup" email?\n\n`
      + 'One email goes out with the unified pickup manifest attached.'
    : `Stage ${readyCount} ready pallet(s) for pickup on ${plan.order_number}?\n\n`
      + 'The customer email will be sent later when the remaining pallet(s) are staged and the SO closes out.';
}

const manifestDoc = (apiBase, plan, url, prefix, what) => ({
  url: `${apiBase}${url}`, filename: `${prefix}${plan.order_number || plan.plan_id}_${shopToday()}.pdf`, what,
});

async function openQuietly(ui, doc) {
  if (!ui.openPdfTab || !doc) return;
  try { await ui.openPdfTab(doc.url, doc.filename); } catch (_) { /* non-fatal: the Finalize dialog offers it again */ }
}

/**
 * Ship every Ready pallet in one request (per-pallet emails forced OFF: one combined notice for this batch). The
 * load-out checklist first, then the PIN, then the question. When the plan auto-advances to Complete the SO-Finalize
 * email opens with the same PIN (openFinalize). A delivery order with delivery signing on goes through Load truck
 * instead: the caller checks features/shippingHub/deliverySigning.deliveryLoadsOn first.
 * Resolves {done, message?, document?, finalize?, recovered?}.
 */
export async function shipAllReady({
  apiBase = API_BASE, plan, salesOrderId = null, readyCount, ui,
  setBusy = noop, setMessage = noop, reloadPlan = noop, openFinalize = noop, recovery = {},
}) {
  if (!plan || !readyCount) return { done: false };
  const opensPdf = !!ui.openPdfTab;
  const soId = salesOrderId ?? plan.sales_order_id;
  const totalCount = notConsolidated(plan).length;
  const willCloseSO = readyCount === totalCount && totalCount > 0;
  // the pallets acted on, before the request: a dropped connection is reconciled against real server state
  const targetPalletIds = (plan.pallets || []).filter((p) => p.status === 'Ready').map((p) => p.pallet_id);
  // the load-out checklist (2026-10-26): every pallet checked off as it goes on the truck, its beacon pulled; sent
  // WITH the ship request (nothing is recorded if the ship is refused)
  setBusy(true); // no second click while the list is open
  const { loadOutChecklist } = await import('../pallets/planPalletFlows');
  const loadOut = await loadOutChecklist({
    planId: plan.plan_id, step: 'ship', palletIds: targetPalletIds, requireAll: true,
    recordLabel: `Loaded: ship ${readyCount} pallet${readyCount === 1 ? '' : 's'}`,
  });
  setBusy(false);
  if (loadOut.cancelled) return { done: false };
  const pinAction = willCloseSO
    ? `ship all ${readyCount} pallet(s) for ${plan.order_number} and email the customer the delivery notification for this batch`
    : `ship ${readyCount} ready pallet(s) for ${plan.order_number} and email the customer the delivery notification for this batch`;
  const pin = await ui.pin(`Enter your 4-digit PIN to ${pinAction}.`);
  if (!pin) return { done: false };
  const question = shipAllQuestion(plan, readyCount, willCloseSO, { opensPdf });
  if (!(await ui.confirm(question, { title: willCloseSO ? `Ship all ${readyCount}?` : `Ship ${readyCount}?`, confirmLabel: 'Ship' }))) {
    return { done: false };
  }
  setBusy(true); setMessage('');
  try {
    // a 409 "Still owes Finish on PLT-x" (a pallet's pieces owe an in-house step) offers a manager override
    const res = await postWithInhouseOverride(
      `/packaging/plans/${plan.plan_id}/ship-all-ready`,
      {
        pin,
        ship_date: shopToday(), // the shop's (Central) day
        // Always false: the one combined email for this batch replaces per-pallet emails.
        notify_customer_per_pallet: false,
        // fulfillment_method omitted: the API resolves it from the SO
        ...(Array.isArray(loadOut.items) ? { load_checklist: loadOut.items } : {}),
      },
      ui, 'ship',
    );
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      const e = new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      // 4xx = the server looked at this and refused it (bad PIN, plan already Complete, nothing Ready). 5xx / gateway
      // statuses mean we don't know whether the work ran: reconcile those.
      e.serverRejected = res.status >= 400 && res.status < 500;
      throw e;
    }
    const body = await res.json();
    const bits = [`Shipped ${body.shipped_pallet_count} pallet(s)`];
    if (body.processed_task_count) bits.push(`${body.processed_task_count} task(s) finalized`);
    if (body.auto_closed_count) bits.push(`${body.auto_closed_count} surplus task(s) auto-closed`);
    const emailNote = batchEmailNote(body, 'manifest');
    if (emailNote) bits.push(emailNote);
    // Today's manifest (just the pallets that shipped in this click, the document the customer email attached). The
    // whole-order document stays behind Finalize SO so it is not mistaken for this shipment's paperwork (SO-1112).
    const batchManifestUrl = body.batch_manifest_url || null;
    const batchPalletLabels = body.batch_pallet_labels || [];
    let document = null;
    if (batchManifestUrl) {
      const n = batchPalletLabels.length || body.shipped_pallet_count;
      bits.push(opensPdf ? `manifest for these ${n} pallet(s) opened` : `manifest for these ${n} pallet(s) ready to open`);
      document = manifestDoc(apiBase, plan, batchManifestUrl, 'Manifest_', `Manifest for these ${n} pallet(s)`);
      await openQuietly(ui, document);
    }
    const message = bits.join(' · ');
    setMessage(message);
    await reloadPlan();
    if (body.plan_completed) {
      // the plan auto-advanced to Complete: the SO-Finalize email (recipients + message) with the same PIN
      const finalize = { kind: 'shipping', pin, preActionBits: bits, batchManifestUrl, batchPalletLabels };
      openFinalize(finalize);
      return { done: true, message, document, finalize };
    }
    return { done: true, message, document };
  } catch (err) {
    if (!isTransportFailure(err)) {
      const message = `Ship all failed: ${errText(err)}`;
      setMessage(message);
      return { done: false, message };
    }
    // the connection dropped before an answer: the server may well have finished the job
    setMessage('Connection dropped before the server answered. Checking whether the shipment actually went through…');
    const outcome = await pollBulkPalletOutcome({
      apiBase, salesOrderId: soId, palletIds: targetPalletIds, terminalStatuses: ['Shipped'],
      onWaiting: (landed, total) => setMessage(
        `Connection dropped before the server answered. Still working — ${landed} of ${total} pallet(s) shipped so far…`,
      ),
      ...recovery,
    });
    if (!outcome) {
      const message = `Ship all failed: ${errText(err)}. The pallets are still unshipped — safe to try again.`;
      setMessage(message);
      await reloadPlan();
      return { done: false, message };
    }
    const recoveredBits = [
      `Shipped ${outcome.landed} pallet(s)`,
      'recovered after the connection dropped — the server completed the job',
    ];
    const message = recoveredBits.join(' · ');
    setMessage(message);
    await reloadPlan();
    // the answer never arrived: rebuild this batch's manifest from the pallets targeted (their groups are on the snapshot)
    const recoveredGroups = ((outcome.snap && outcome.snap.pallets) || [])
      .filter((p) => targetPalletIds.includes(p.pallet_id) && p.shipment_group_id)
      .map((p) => p.shipment_group_id);
    const recoveredManifestUrl = recoveredGroups.length
      ? `/packaging/plans/${plan.plan_id}/manifest.pdf?groups=${encodeURIComponent(recoveredGroups.join(','))}`
      : null;
    let document = null;
    if (recoveredManifestUrl) {
      document = manifestDoc(apiBase, plan, recoveredManifestUrl, 'Manifest_', `Manifest for these ${outcome.landed} pallet(s)`);
      await openQuietly(ui, document);
    }
    let finalize = null;
    if (outcome.snap?.status === 'Complete') {
      finalize = { kind: 'shipping', pin, preActionBits: recoveredBits, batchManifestUrl: recoveredManifestUrl, batchPalletLabels: [] };
      openFinalize(finalize);
    }
    return { done: true, message, document, finalize, recovered: true };
  } finally {
    setBusy(false);
  }
}

/**
 * Stage every Ready pallet for customer pickup in one request (per-pallet emails forced OFF). When the plan
 * auto-advances to ReadyForPickup the SO-Finalize (pickup) email opens with the same PIN.
 * Resolves {done, message?, document?, finalize?, recovered?}.
 */
export async function stageAllReady({
  apiBase = API_BASE, plan, salesOrderId = null, readyCount, ui,
  setBusy = noop, setMessage = noop, reloadPlan = noop, openFinalize = noop, recovery = {},
}) {
  if (!plan) return { done: false };
  const opensPdf = !!ui.openPdfTab;
  const soId = salesOrderId ?? plan.sales_order_id;
  const totalCount = notConsolidated(plan).length;
  const willCloseSO = readyCount === totalCount && totalCount > 0;
  const targetPalletIds = (plan.pallets || []).filter((p) => p.status === 'Ready').map((p) => p.pallet_id);
  const pinAction = willCloseSO
    ? `mark all ${readyCount} pallet(s) ready for pickup on ${plan.order_number} and notify the customer`
    : `mark ${readyCount} ready pallet(s) for pickup on ${plan.order_number}`;
  const pin = await ui.pin(`Enter your 4-digit PIN to ${pinAction}.`);
  if (!pin) return { done: false };
  const question = stageAllQuestion(plan, readyCount, willCloseSO);
  if (!(await ui.confirm(question, { title: 'Mark ready for pickup?', confirmLabel: 'Mark ready' }))) return { done: false };
  setBusy(true); setMessage('');
  try {
    const res = await postWithInhouseOverride(
      `/packaging/plans/${plan.plan_id}/stage-all-ready`,
      {
        pin,
        ship_date: shopToday(), // the shop's (Central) day
        // Always false: the consolidated email at SO-Finalize-Pickup replaces per-pallet emails.
        notify_customer_per_pallet: false,
      },
      ui, 'stage',
    );
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      const e = new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      e.serverRejected = res.status >= 400 && res.status < 500;
      throw e;
    }
    const body = await res.json();
    const bits = [`Staged ${body.staged_pallet_count} pallet(s) for pickup`];
    if (body.processed_task_count) bits.push(`${body.processed_task_count} task(s) finalized`);
    if (body.auto_closed_count) bits.push(`${body.auto_closed_count} surplus task(s) auto-closed`);
    const emailNote = batchEmailNote(body, 'pickup manifest');
    if (emailNote) bits.push(emailNote);
    // the pickup manifest for just the pallets staged in this click (what the customer email attached)
    const batchManifestUrl = body.batch_manifest_url || null;
    const batchPalletLabels = body.batch_pallet_labels || [];
    let document = null;
    if (batchManifestUrl) {
      const n = batchPalletLabels.length || body.staged_pallet_count;
      bits.push(opensPdf ? `pickup manifest for these ${n} pallet(s) opened` : `pickup manifest for these ${n} pallet(s) ready to open`);
      document = manifestDoc(apiBase, plan, batchManifestUrl, 'Pickup_Manifest_', `Pickup manifest for these ${n} pallet(s)`);
      await openQuietly(ui, document);
    }
    const message = bits.join(' · ');
    setMessage(message);
    await reloadPlan();
    if (body.plan_ready_for_pickup) {
      // the plan auto-advanced to ReadyForPickup: the SO-Finalize email (pickup) with the same PIN
      const finalize = { kind: 'pickup', pin, preActionBits: bits, batchManifestUrl, batchPalletLabels };
      openFinalize(finalize);
      return { done: true, message, document, finalize };
    }
    return { done: true, message, document };
  } catch (err) {
    if (!isTransportFailure(err)) {
      const message = `Stage all failed: ${errText(err)}`;
      setMessage(message);
      return { done: false, message };
    }
    setMessage('Connection dropped before the server answered. Checking whether the pallets were staged…');
    const outcome = await pollBulkPalletOutcome({
      apiBase, salesOrderId: soId, palletIds: targetPalletIds, terminalStatuses: ['Staged', 'Shipped'],
      onWaiting: (landed, total) => setMessage(
        `Connection dropped before the server answered. Still working — ${landed} of ${total} pallet(s) staged so far…`,
      ),
      ...recovery,
    });
    if (!outcome) {
      const message = `Stage all failed: ${errText(err)}. The pallets are still unstaged — safe to try again.`;
      setMessage(message);
      await reloadPlan();
      return { done: false, message };
    }
    const recoveredBits = [
      `Staged ${outcome.landed} pallet(s) for pickup`,
      'recovered after the connection dropped — the server completed the job',
    ];
    const message = recoveredBits.join(' · ');
    setMessage(message);
    await reloadPlan();
    let finalize = null;
    if (outcome.snap?.status === 'ReadyForPickup') {
      finalize = { kind: 'pickup', pin, preActionBits: recoveredBits };
      openFinalize(finalize);
    }
    return { done: true, message, finalize, recovered: true };
  } finally {
    setBusy(false);
  }
}

// --- Part labels -------------------------------------------------------------------------------
// The rich part labels (image + material / finish / dimensions / weight / qty) for a sealed or shipped pallet:
// GET /reports/part_labels/batch/{group}?pallet_number=N (pallet-direct items AND per-box items). The desktop opens
// the PDF; a phone queues it at its label station (an image-heavy multi-page PDF: docs "Print audit" item 4).

export const partLabelsPath = (shipmentGroupId, palletNum) => `/reports/part_labels/batch/${shipmentGroupId}?pallet_number=${palletNum}`;

const PHONE_TRIGGER = 'phone_package_guide';

/** One pallet's part labels. Resolves {done, message?}. */
export async function printPartLabels({
  apiBase = API_BASE, shipmentGroupId, palletNum, palletName = null, ui, setBusy = noop, setMessage = noop,
}) {
  if (!shipmentGroupId) return { done: false };
  setBusy(true); setMessage('');
  try {
    const path = partLabelsPath(shipmentGroupId, palletNum);
    if (ui.openPdfTab) {
      await ui.openPdfTab(`${apiBase}${path}`, `PartLabels_P${palletNum}.pdf`);
      return { done: true };
    }
    const r = await printLabelsAt({
      station: ui.labelStation, path, kind: 'part_labels', what: `${palletName || `P${palletNum}`} part labels`, trigger: PHONE_TRIGGER,
    });
    setMessage(r.message);
    return { done: true, message: r.message };
  } catch (err) {
    const message = `Print part labels failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

export const NO_SEALED_PALLETS = 'No sealed pallets yet — seal at least one before printing part labels.';

/** Part labels for every sealed pallet on the plan: one PDF per pallet (a failed one doesn't stop the rest). */
export async function printAllPartLabels({ apiBase = API_BASE, plan, ui, setBusy = noop, setMessage = noop }) {
  if (!plan) return { done: false };
  const sealed = sealedPallets(plan);
  if (!sealed.length) {
    setMessage(NO_SEALED_PALLETS);
    return { done: false, message: NO_SEALED_PALLETS };
  }
  setBusy(true); setMessage('');
  try {
    if (ui.openPdfTab) {
      for (const p of sealed) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await ui.openPdfTab(`${apiBase}${partLabelsPath(p.shipment_group_id, p.pallet_num)}`, `PartLabels_${p.short_id || `P${p.pallet_num}`}.pdf`);
        } catch (_) { /* per-pallet failure is non-fatal */ }
      }
      const message = `Opened part-label PDFs for ${sealed.length} pallet(s).`;
      setMessage(message);
      return { done: true, message };
    }
    let sent = 0;
    let printer = null;
    const failed = [];
    for (const p of sealed) {
      const name = p.short_id || `P${p.pallet_num}`;
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await printLabelsAt({
          station: ui.labelStation, path: partLabelsPath(p.shipment_group_id, p.pallet_num), kind: 'part_labels',
          what: `${name} part labels`, trigger: PHONE_TRIGGER,
        });
        sent += 1;
        printer = r.message;
      } catch (_) {
        failed.push(name);
      }
    }
    const bits = [];
    if (sent) bits.push(sent === 1 ? printer : `Part labels for ${sent} pallet(s) sent to the printer.`);
    if (failed.length) bits.push(`Not sent: ${failed.join(', ')}.`);
    const message = bits.join(' ');
    setMessage(message);
    return { done: sent > 0 && !failed.length, message };
  } finally {
    setBusy(false);
  }
}

// --- Combined manifest + the finalize result -------------------------------------------------------

/** The COMBINED shipment manifest (driver / pickup view): every physical pallet the plan touches, ride-alongs too. */
export function combinedManifestDocument(plan, apiBase = API_BASE) {
  return {
    url: `${apiBase}/packaging/plans/${plan.plan_id}/combined-manifest.pdf`,
    filename: `Combined_Shipment_${plan.order_number || plan.plan_id}.pdf`,
    what: 'Combined manifest (every pallet)',
  };
}

/** Reprint the combined manifest (desktop: opens it; a phone gets it back as `document`). */
export async function reprintCombinedManifest({ apiBase = API_BASE, plan, ui, setBusy = noop, setMessage = noop }) {
  if (!plan) return { done: false };
  setBusy(true); setMessage('');
  try {
    const document = combinedManifestDocument(plan, apiBase);
    if (ui.openPdfTab) await ui.openPdfTab(document.url, document.filename);
    return { done: true, document };
  } catch (err) {
    const message = `Reprint combined manifest failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

/** What the page says after the SO-Finalize email went (the dialog's onSent), and the manifest it hands over
 *  ({message, document}). `opensPdf` false (a phone): the manifest is offered, not opened. */
export function finalizeSentResult({ apiBase = API_BASE, plan, kind, preActionBits, responseBody, notify, opensPdf = true }) {
  const bits = [...(preActionBits || [])];
  const action = kind === 'pickup' ? 'pickup finalized' : 'shipment finalized';
  if (responseBody.already_finalized) {
    bits.push('SO already finalized');
  } else {
    bits.push(`SO ${plan?.order_number || ''} ${action}`);
    if (responseBody.batch_finalize) bits.push(`new batch: ${(responseBody.batch_pallets || []).join(', ')}`);
    bits.push(finalizeEmailNote(responseBody, notify));
  }
  if (responseBody.manifest_url && !responseBody.already_finalized) {
    const verb = opensPdf ? 'opened' : 'ready to open';
    bits.push(responseBody.batch_finalize
      ? `new-batch manifest ${verb}`
      : `consolidated manifest ${verb} (every pallet on the order, all ship dates)`);
  }
  let document = null;
  if (responseBody.manifest_url) {
    // manifest_url is batch-scoped (?groups=) on a batch finalize, so it matches the customer's email attachment
    const batchTag = responseBody.batch_finalize ? '_batch' : '_all_pallets';
    const filename = kind === 'pickup'
      ? `Pickup_Manifest_${plan?.order_number || plan?.plan_id}${batchTag}.pdf`
      : `SO_Manifest_${plan?.order_number || plan?.plan_id}${batchTag}.pdf`;
    document = {
      url: `${apiBase}${responseBody.manifest_url}`, filename,
      what: responseBody.batch_finalize ? 'New-batch manifest' : 'Consolidated manifest (every pallet)',
    };
  }
  return { message: bits.join(' · '), document };
}
